import { setParameterInSelection, removeParameterFromSelection } from "../tools"

/**
 * Enhanced color plugin with custom color support
 * @param {*} param0
 */
const Color = ({ action, data = {} }) => {
  // Legacy red/blue actions (keeping for backward compatibility)
  if (action === "red") {
    setParameterInSelection("ae-color-red")
    return
  }

  if (action === "blue") {
    setParameterInSelection("ae-color-blue")
    return
  }

  // New custom color actions
  if (action === "apply") {
    const { color } = data
    if (!color) return

    // Check if it's a CSS class
    if (color.startsWith('css-class:')) {
      const className = color.replace('css-class:', '')
      // Remove any existing color styles and classes
      removeColorClasses()
      removeColorStyles()
      // Apply CSS class
      applyCSSClass(className)
      return
    }

    // Remove any existing color classes
    removeColorClasses()

    // Apply inline style for custom colors
    applyColorStyle(color)
    return
  }

  if (action === "remove") {
    removeColorClasses()
    removeColorStyles()
    return
  }
}

// Helper function to remove existing color classes
const removeColorClasses = () => {
  const selection = window.getSelection()
  if (selection.rangeCount === 0) return

  const range = selection.getRangeAt(0)
  const selectedElements = getSelectedElements(range)
  
  selectedElements.forEach(element => {
    // Remove ae-color-* classes
    const classes = Array.from(element.classList)
    classes.forEach(className => {
      if (className.startsWith('ae-color-')) {
        element.classList.remove(className)
      }
    })
  })
}

// Helper function to remove color styles
const removeColorStyles = () => {
  const selection = window.getSelection()
  if (selection.rangeCount === 0) return

  const range = selection.getRangeAt(0)
  const selectedElements = getSelectedElements(range)
  
  selectedElements.forEach(element => {
    element.style.color = ''
    if (element.style.length === 0) {
      element.removeAttribute('style')
    }
  })
}

// Helper function to apply CSS class
const applyCSSClass = (className) => {
  const selection = window.getSelection()
  if (selection.rangeCount === 0) return

  const range = selection.getRangeAt(0)
  
  if (range.collapsed) return // No selection

  // If no text is selected, wrap the cursor position
  if (range.toString().length === 0) return

  // Create a span with the CSS class
  const span = document.createElement('span')
  span.className = className
  
  try {
    // Surround the selection with the span
    range.surroundContents(span)
  } catch (error) {
    // If surroundContents fails (e.g., selection spans multiple elements)
    // Extract contents and wrap them
    const contents = range.extractContents()
    span.appendChild(contents)
    range.insertNode(span)
  }

  // Clear selection
  selection.removeAllRanges()
  
  // Set cursor after the colored text
  const newRange = document.createRange()
  newRange.setStartAfter(span)
  newRange.collapse(true)
  selection.addRange(newRange)
}

// Helper function to apply color style
const applyColorStyle = (color) => {
  const selection = window.getSelection()
  if (selection.rangeCount === 0) return

  const range = selection.getRangeAt(0)
  
  if (range.collapsed) return // No selection

  // If no text is selected, wrap the cursor position
  if (range.toString().length === 0) return

  // Create a span with the color
  const span = document.createElement('span')
  span.style.color = color
  span.className = 'ae-custom-color'
  
  try {
    // Surround the selection with the span
    range.surroundContents(span)
  } catch (error) {
    // If surroundContents fails (e.g., selection spans multiple elements)
    // Extract contents and wrap them
    const contents = range.extractContents()
    span.appendChild(contents)
    range.insertNode(span)
  }

  // Clear selection
  selection.removeAllRanges()
  
  // Set cursor after the colored text
  const newRange = document.createRange()
  newRange.setStartAfter(span)
  newRange.collapse(true)
  selection.addRange(newRange)
}

// Helper function to get selected elements
const getSelectedElements = (range) => {
  const elements = []
  const container = range.commonAncestorContainer
  
  if (container.nodeType === Node.ELEMENT_NODE) {
    elements.push(container)
  } else if (container.parentElement) {
    elements.push(container.parentElement)
  }

  // Get all elements within the selection
  if (container.nodeType === Node.ELEMENT_NODE) {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          return range.intersectsNode(node) ? 
            NodeFilter.FILTER_ACCEPT : 
            NodeFilter.FILTER_REJECT
        }
      }
    )

    let node
    while (node = walker.nextNode()) {
      elements.push(node)
    }
  }

  return elements
}

export default Color
